import * as THREE from 'three'
import {
  CanonicalCut,
  findIPOptimalSolution,
  findIPOptimalValue,
  generateAllCuts,
  getCutInfo,
  IntegerProblem,
} from '@/core/cuts'
import { ProblemManager } from '@/game/problem-manager'
import { computeScore } from '@/game/score-service'
import { uiManager, type CutHistoryItem } from '@/game/ui-manager'
import { appBus } from '@/app/app-events'
import { logger } from '@/app/logger'
import { mathRenderer } from '@/game/math-panel-adapter'
import { useGameStore } from '@/react/state/gameStore'
import { findIntegerPointsInPolyhedron } from '@/graphics/geometry'
import { cloneIntegerProblem } from '@/game/engine/problem-clone'

export interface SessionEngineContext {
  scene: THREE.Scene
  polyhedronGroup: THREE.Group | null
  currentProblem: IntegerProblem | null
  originalProblem: IntegerProblem | null
  originalCustomProblem: IntegerProblem | null
  availableCuts: CanonicalCut[]
  cutsHistory: CutHistoryItem[]
  cutsRemaining: number
  customCutsLimit: number
  initialCutsGenerated: boolean
  initialObjectiveValue: number
  initialIPOptimalValue: number
  initialIPOptimalSolution: number[]
  initialVolume: number
  cutTypeCounters: Map<string, number>
  pendingCut: CanonicalCut | null
  pendingCutIndex: number
  currentCuttingPlane: unknown | null
  appBusUnsubscribers: Array<() => void>
  boundWindowResizeHandler: (() => void) | null
  boundWindowKeydownHandler: ((event: KeyboardEvent) => void) | null
  updateCutsHistoryPanel(): void
  runAlgorithmWithProblem(problem: IntegerProblem): void
  applyCut(cutIndex: number): Promise<void>
  confirmCut(isSimplex?: boolean): Promise<void>
  cancelCut(isSimplex?: boolean): void
  handleCutMuChange(mu: number): void
  refreshAvailableCuts(): Promise<void>
  generateNewProblem(): Promise<void>
  updateUI(): void
  toggleAllPanels(): void
  handleResize(): void
  resetCamera(): void
  toggleAnimation(): void
  clearUserCuts(): void
  createGameProblem(): Promise<void>
  calculateCurrentPolyhedronVolume(): number
  generateCutsForProblem(problem: IntegerProblem): void
  generateStrongerCuts(): CanonicalCut[]
  clearVisualization(): void
  clearStaleData(): void
  generateCompleteVisualization(problem: IntegerProblem): void
  refreshCutButtons(): void
  updateCutsLimit(newLimit: number): void
  cleanupCuttingPlane(): void
}

export async function createGameProblem(engine: SessionEngineContext): Promise<void> {
  logger.log('Starting createGameProblem...')
  uiManager.showLoading(true)

  try {
    logger.log('Creating 3D problem...')
    engine.currentProblem = ProblemManager.createDefault3DProblem()
    useGameStore.getState().setCurrentProblem(engine.currentProblem)
    logger.log('Generated 3D problem:', engine.currentProblem)

    engine.initialObjectiveValue = getCutInfo(engine.currentProblem).optimalValue
    logger.log('Initial objective value recorded:', engine.initialObjectiveValue)

    engine.initialIPOptimalValue = findIPOptimalValue(engine.currentProblem)
    logger.log('Initial IP optimal value recorded:', engine.initialIPOptimalValue)

    engine.initialIPOptimalSolution = findIPOptimalSolution(engine.currentProblem)
    logger.log('Initial IP optimal solution recorded:', engine.initialIPOptimalSolution)

    engine.cutsRemaining = engine.customCutsLimit
    engine.updateCutsHistoryPanel()

    if (!engine.originalCustomProblem) {
      mathRenderer.renderProblem(engine.currentProblem)
    }
    mathRenderer.show()

    uiManager.reinitializePanelToggles()
    uiManager.showNotification('Problem created. Click "Customize" to edit or the problem will run with default settings.', 'info')

    engine.runAlgorithmWithProblem(engine.currentProblem)
    logger.log('createGameProblem completed successfully')
  } catch (error) {
    logger.error('An error occurred during problem creation:', error)
    uiManager.showNotification('Failed to create problem. Check console for details.', 'error')
  } finally {
    uiManager.showLoading(false)
  }
}

export function setupEventListeners(engine: SessionEngineContext): void {
  engine.boundWindowResizeHandler = () => engine.handleResize()
  window.addEventListener('resize', engine.boundWindowResizeHandler)

  engine.boundWindowKeydownHandler = (event: KeyboardEvent) => {
    switch (event.key.toLowerCase()) {
      case 'r':
        engine.resetCamera()
        break
      case ' ':
        engine.toggleAnimation()
        break
      case 'n':
        void engine.generateNewProblem()
        break
      case 'h':
        if (event.shiftKey) {
          uiManager.expandAllPanels()
          uiManager.showNotification('📤 All panels expanded (Shift+H)', 'info')
        } else {
          engine.toggleAllPanels()
        }
        break
      case 'c':
        break
    }
  }
  window.addEventListener('keydown', engine.boundWindowKeydownHandler)

  const unsubscribeCutSelected = appBus.on('cut-select-requested', ({ cutIndex }) => engine.applyCut(cutIndex))
  const unsubscribeCutConfirm = appBus.on('cut-confirm-requested', () => {
    useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_CONFIRMED' })
    engine.confirmCut()
  })
  const unsubscribeCutCancel = appBus.on('cut-cancel-requested', () => {
    engine.cancelCut()
  })
  const unsubscribeCutMuChange = appBus.on('cut-mu-change-requested', ({ mu }) => {
    engine.handleCutMuChange(mu)
  })
  const unsubscribeRefreshCuts = appBus.on('refresh-cuts', () => engine.refreshAvailableCuts())
  const unsubscribeResetGame = appBus.on('reset-game', () => {
    void engine.generateNewProblem()
  })
  const unsubscribeHistoryUpdated = appBus.on('history-updated', ({ history }) => {
    if (!engine.currentProblem) return
    uiManager.updateCutsHistoryPanel(
      history,
      engine.initialObjectiveValue,
      engine.initialIPOptimalValue,
      engine.initialIPOptimalSolution,
    )
  })
  const unsubscribeCutApplied = appBus.on('cut-applied', () => engine.updateUI())

  engine.appBusUnsubscribers.push(
    unsubscribeCutSelected,
    unsubscribeCutConfirm,
    unsubscribeCutCancel,
    unsubscribeCutMuChange,
    unsubscribeRefreshCuts,
    unsubscribeResetGame,
    unsubscribeHistoryUpdated,
    unsubscribeCutApplied,
  )
}

export function toggleAllPanels(engine: SessionEngineContext): void {
  const panelState = useGameStore.getState().ui.panels
  const anyExpanded = ['math', 'cut', 'history', 'bnb'].some(
    (panel) => panelState[panel as keyof typeof panelState] === 'expanded',
  )

  if (anyExpanded) {
    uiManager.minimizeAllPanels()
    uiManager.showNotification('📥 All panels minimized (H)', 'info')
  } else {
    uiManager.expandAllPanels()
    uiManager.showNotification('📤 All panels expanded (H)', 'info')
  }
}

export function generateStrongerCuts(engine: SessionEngineContext): CanonicalCut[] {
  logger.log('Generating stronger artificial cuts for demonstration...')

  const currentPoints = engine.currentProblem
    ? findIntegerPointsInPolyhedron(engine.currentProblem.inequalities, { min: 0, max: 4 })
    : []

  logger.log('Current integer points:', currentPoints.map((p) => `(${p.x},${p.y},${p.z})`))

  let xLP: number[] = []
  if (engine.currentProblem) {
    try {
      const cutInfo = getCutInfo(engine.currentProblem)
      xLP = cutInfo.optimalSolution
      logger.log('LP optimal solution for efficacy calculation:', xLP)
    } catch (error) {
      logger.warn('Failed to get optimal solution for efficacy calculation:', error)
      xLP = [2.5, 2.5, 2.5]
    }
  } else {
    xLP = [2.5, 2.5, 2.5]
  }

  const calculateEfficacy = (alpha: number[], beta: number): number => {
    let alphaTransposeXLP = 0
    for (let i = 0; i < alpha.length && i < xLP.length; i++) {
      alphaTransposeXLP += alpha[i] * xLP[i]
    }

    const l2NormSquared = alpha.reduce((sum, coeff) => sum + coeff * coeff, 0)
    const l2Norm = Math.sqrt(l2NormSquared)
    const efficacy = l2Norm > 0 ? Math.abs(alphaTransposeXLP - beta) / l2Norm : 0

    logger.log(`Efficacy calculation for cut [${alpha.join(', ')}] <= ${beta}:`)
    logger.log(`  α^T x_LP = ${alphaTransposeXLP.toFixed(3)}`)
    logger.log(`  ||α||_2 = ${l2Norm.toFixed(3)}`)
    logger.log(`  efficacy = ${efficacy.toFixed(3)}`)

    return efficacy
  }

  const strongerCuts: CanonicalCut[] = [
    {
      alpha: [1, 1, 1],
      beta: 2,
      efficacy: calculateEfficacy([1, 1, 1], 2),
      sourceRow: 0,
      cgfType: 'gmi',
    },
    {
      alpha: [1, 1, 0],
      beta: 2,
      efficacy: calculateEfficacy([1, 1, 0], 2),
      sourceRow: 1,
      cgfType: 'gmi',
    },
    {
      alpha: [1, 0, 0],
      beta: 1,
      efficacy: calculateEfficacy([1, 0, 0], 1),
      sourceRow: 2,
      cgfType: 'gomory',
    },
    {
      alpha: [0, 1, 0],
      beta: 1,
      efficacy: calculateEfficacy([0, 1, 0], 1),
      sourceRow: 0,
      cgfType: 'gmi',
    },
    {
      alpha: [1, 0, 1],
      beta: 2,
      efficacy: calculateEfficacy([1, 0, 1], 2),
      sourceRow: 1,
      cgfType: 'gomory',
    },
  ]

  logger.log('Generated stronger cuts:')
  strongerCuts.forEach((cut, index) => {
    logger.log(`Cut ${index + 1}: ${cut.alpha.join('*x + ')}*x <= ${cut.beta}`)

    let eliminatedCount = 0
    currentPoints.forEach((point) => {
      const value = cut.alpha[0] * point.x + cut.alpha[1] * point.y + cut.alpha[2] * point.z
      if (value > cut.beta) {
        eliminatedCount++
      }
    })
    logger.log(`  → Will eliminate ${eliminatedCount} points`)
  })

  return strongerCuts
}

export async function generateNewProblem(engine: SessionEngineContext): Promise<void> {
  logger.log('Generating new problem...')
  uiManager.showNotification('Generating new problem...', 'info')
  engine.cleanupCuttingPlane()

  if (engine.polyhedronGroup) {
    engine.scene.remove(engine.polyhedronGroup)
    engine.polyhedronGroup.traverse((child: THREE.Object3D) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose())
        } else {
          (child.material as THREE.Material).dispose()
        }
      }
    })
    engine.polyhedronGroup = null
  }

  engine.cutsHistory = []
  engine.updateCutsHistoryPanel()
  engine.clearUserCuts()

  engine.initialCutsGenerated = false
  engine.cutsRemaining = engine.customCutsLimit
  engine.availableCuts = []
  engine.originalCustomProblem = null

  try {
    await engine.createGameProblem()
    logger.log('New problem generated!')
    uiManager.showNotification('New problem generated!', 'success')
  } catch (error) {
    logger.error('Failed to generate new problem:', error)
    uiManager.showNotification('Failed to generate new problem.', 'error')
  }
}

export function endGame(engine: SessionEngineContext): void {
  const finalVolume = engine.calculateCurrentPolyhedronVolume()
  const finalReduction = engine.initialVolume > 0 ? ((engine.initialVolume - finalVolume) / engine.initialVolume) * 100 : 0

  let finalScore = 0
  if (engine.currentProblem && engine.initialObjectiveValue > 0 && engine.initialIPOptimalValue >= 0) {
    const currentObjectiveValue = getCutInfo(engine.currentProblem).optimalValue
    const lpReductionFactor =
      engine.initialObjectiveValue !== engine.initialIPOptimalValue
        ? (engine.initialObjectiveValue - currentObjectiveValue) / (engine.initialObjectiveValue - engine.initialIPOptimalValue)
        : 0
    const volumeReductionFactor = finalReduction / 100
    finalScore = Math.round(100 * lpReductionFactor + 500 * volumeReductionFactor)
  } else {
    finalScore = Math.round(finalReduction * 10)
  }

  uiManager.showGameOverModal(finalScore, finalReduction)
  uiManager.disableCutButtons()
}

export function updateUI(engine: SessionEngineContext): void {
  const currentVolume = engine.calculateCurrentPolyhedronVolume()
  const volumeReduction = engine.initialVolume > 0 ? ((engine.initialVolume - currentVolume) / engine.initialVolume) * 100 : 0

  const currentObjectiveValue = engine.currentProblem ? getCutInfo(engine.currentProblem).optimalValue : 0
  const score = computeScore({
    initialObjectiveValue: engine.initialObjectiveValue,
    initialIPOptimalValue: engine.initialIPOptimalValue,
    initialVolume: engine.initialVolume,
    currentObjectiveValue,
    currentVolume,
  })

  uiManager.updateStats(engine.cutsRemaining, volumeReduction, score)

  if (engine.cutsRemaining <= 0) {
    uiManager.disableCutButtons()
  }
}

export function handleProblemUpdate(engine: SessionEngineContext, problem: IntegerProblem): void {
  logger.log('[Main] Handling problem update:', problem)

  engine.currentProblem = problem
  useGameStore.getState().setCurrentProblem(engine.currentProblem)

  engine.originalCustomProblem = problem

  engine.clearVisualization()

  engine.initialCutsGenerated = false
  logger.log('Reset initialCutsGenerated flag for new customized problem')

  engine.cutsHistory = []
  engine.updateCutsHistoryPanel()
  engine.clearUserCuts()

  engine.runAlgorithmWithProblem(problem)
}

export function runAlgorithmWithProblem(engine: SessionEngineContext, problem: IntegerProblem): void {
  logger.log('Running algorithm with new problem:', problem)

  try {
    logger.log('Preprocessing constraints...')
    const cleanedProblem = problem
    const analysis = { removedCount: 0, originalCount: problem.A.length }

    if (analysis.removedCount > 0) {
      logger.log(`🧹 Redundancy Analysis: Removed ${analysis.removedCount}/${analysis.originalCount} constraints`)
      logger.log('   Original problem size:', problem.A.length, 'constraints')
      logger.log('   Cleaned problem size:', cleanedProblem.A.length, 'constraints')
      uiManager.showNotification(
        `Removed ${analysis.removedCount} redundant constraints from ${analysis.originalCount} total constraints.`,
        'info',
      )
    } else {
      logger.log(`🧹 Redundancy Analysis: No redundant constraints found (${analysis.originalCount} constraints)`)
    }

    engine.currentProblem = cleanedProblem

    requestAnimationFrame(() => {
      mathRenderer.safeRerenderMath()
    })

    engine.clearStaleData()
    engine.generateCompleteVisualization(cleanedProblem)

    engine.initialVolume = engine.calculateCurrentPolyhedronVolume()
    logger.log('Initial volume recorded:', engine.initialVolume)

    const cutInfo = getCutInfo(cleanedProblem)
    engine.initialObjectiveValue = cutInfo.optimalValue
    engine.initialIPOptimalValue = findIPOptimalValue(cleanedProblem)
    engine.initialIPOptimalSolution = findIPOptimalSolution(cleanedProblem)

    logger.log(`Initial objective value: ${engine.initialObjectiveValue}`)
    logger.log(`Initial IP optimal value: ${engine.initialIPOptimalValue}`)
    logger.log(`Initial IP optimal solution: ${engine.initialIPOptimalSolution}`)
    logger.log(`Final constraint count: ${cleanedProblem.A.length}`)

    engine.generateCutsForProblem(cleanedProblem)
    engine.updateUI()

    logger.log('Algorithm completed successfully')
    uiManager.showNotification('Problem updated successfully!', 'success')
  } catch (error) {
    logger.error('Error running algorithm:', error)
    uiManager.showNotification('Failed to update problem. Check console for details.', 'error')
  }
}

export function generateCutsForProblem(engine: SessionEngineContext, problem: IntegerProblem): void {
  logger.log('Generating cuts for problem...')

  if (engine.initialCutsGenerated) {
    logger.log('Initial cuts already generated, skipping...')
    return
  }

  engine.originalProblem = cloneIntegerProblem(problem, { includeFracData: false })

  try {
    const lpSolutionCheck = getCutInfo(problem)
    logger.log('LP relaxation check:', lpSolutionCheck)

    if (lpSolutionCheck.hasFractionalSolution) {
      const { cuts } = generateAllCuts(problem, ['gmi', 'gomory', '2dsimplex'])
      engine.availableCuts = cuts

      const cutsByType = cuts.reduce((acc, cut) => {
        acc[cut.cgfType] = (acc[cut.cgfType] || 0) + 1
        return acc
      }, {} as Record<string, number>)

      logger.log('🔪 Cut Generation Summary:')
      Object.entries(cutsByType).forEach(([type, count]) => {
        logger.log(`   ${type}: ${count} cuts`)
      })

      engine.availableCuts = cuts
      logger.log(`   Total cuts available: ${engine.availableCuts.length}`)
    } else {
      logger.warn('LP relaxation already has integer solution! Generating stronger cuts for demonstration.')
      engine.availableCuts = engine.generateStrongerCuts()
      uiManager.showNotification('LP solution is integer. Using demonstration cuts.', 'info')
    }

    engine.initialCutsGenerated = true

    logger.log('Generated', engine.availableCuts.length, 'cuts')
    uiManager.createCutButtons(engine.availableCuts)
  } catch (error) {
    logger.error('Error generating cuts:', error)
    engine.availableCuts = engine.generateStrongerCuts()
    engine.initialCutsGenerated = true
    uiManager.createCutButtons(engine.availableCuts)
  }
}

export function updateCutsHistoryPanel(engine: SessionEngineContext): void {
  if (!engine.currentProblem) return
  uiManager.updateCutsHistoryPanel(
    engine.cutsHistory,
    engine.initialObjectiveValue,
    engine.initialIPOptimalValue,
    engine.initialIPOptimalSolution,
  )
}

export function setupUserCutEventListeners(engine: SessionEngineContext): void {
  const unsubscribeUserCutAdded = appBus.on('user-cut-added', ({ cut }) => {
    logger.log('User cut added:', cut)
    engine.availableCuts.push(cut)
    engine.refreshCutButtons()
  })
  engine.appBusUnsubscribers.push(unsubscribeUserCutAdded)
}

export function refreshCutButtons(engine: SessionEngineContext): void {
  if (!engine.currentProblem) return

  const allCuts = [...engine.availableCuts]
  uiManager.createCutButtons(allCuts)
}

export function loadCutsLimitFromStorage(engine: SessionEngineContext): void {
  const savedLimit = localStorage.getItem('cutsLimit')
  if (savedLimit) {
    const limit = parseInt(savedLimit)
    if (!isNaN(limit) && limit > 0 && limit <= 50) {
      engine.customCutsLimit = limit
    }
  }
  engine.cutsRemaining = engine.customCutsLimit
}

export function saveCutsLimitToStorage(_engine: SessionEngineContext, limit: number): void {
  localStorage.setItem('cutsLimit', limit.toString())
}

export function setupCutsLimitConfiguration(engine: SessionEngineContext): void {
  uiManager.initializeCutsLimitConfiguration(engine.customCutsLimit, (newLimit: number) => {
    engine.updateCutsLimit(newLimit)
  })
}

export function updateCutsLimit(engine: SessionEngineContext, newLimit: number): void {
  engine.customCutsLimit = newLimit
  engine.cutsRemaining = newLimit
  saveCutsLimitToStorage(engine, newLimit)
  engine.updateUI()
  logger.log(`Cuts limit updated to ${newLimit}`)
}

export function clearUserCuts(engine: SessionEngineContext): void {
  uiManager.clearUserCuts()

  engine.availableCuts = engine.availableCuts.filter((cut: CanonicalCut) => cut.cgfType !== 'user')
  engine.cutTypeCounters.delete('USER')
  engine.refreshCutButtons()
}

export function clearStaleData(engine: SessionEngineContext): void {
  logger.log('Clearing stale data from previous problem...')
  appBus.emit('bnb-reset-requested', {})

  engine.availableCuts = []
  engine.cutsHistory = []
  engine.cutTypeCounters.clear()

  engine.cleanupCuttingPlane()
  engine.currentCuttingPlane = null
  engine.pendingCut = null
  engine.pendingCutIndex = -1
  ;(engine as any).pendingSimplexConfig = null
  engine.initialCutsGenerated = false

  uiManager.clearUserCuts()
  logger.log('Stale data cleared successfully')
}
